// =============================================================================
// QuestionButtons.tsx — Inline question buttons with answer options (web port)
//
// When a single AI message contains multiple questions, answers are batched:
// the user selects an answer for each question, and a "Submit Answers" button
// sends them all at once. Single-question messages send immediately on tap.
//
// Photo tasks show per-question attachment thumbnails below the answer buttons.
// Answer options are mutually exclusive — selecting one deselects others.
// "Attach photo" is special: it opens the file picker and stays highlighted
// only while photos are attached.
// =============================================================================

import React, { useCallback, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import CloseIcon from '@mui/icons-material/Close';
import ReplayIcon from '@mui/icons-material/Replay';
import { useTheme } from '../../context/PermissionsContext/ThemeContext/ThemeContext';
import type {
  QuestionObject,
  AttachmentItem,
} from '../../services/notesToHank/NotesToHankTypes';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface QuestionButtonsProps {
  questions: QuestionObject[];
  onAnswerSelect: (
    questionIndex: number,
    answerIndex: number,
    answerId: string,
    answerText: string,
    questionId?: string
  ) => void;
  onBatchSubmit?: (
    answerIds: string[],
    combinedText: string,
    selectedAnswersMap: Record<string, string>
  ) => void;
  selectedAnswers: Record<string, string>;
  questionAttachments?: Record<string, AttachmentItem[]>;
  onRemoveQuestionAttachment?: (questionId: string, index: number) => void;
  onRetryUpload?: (questionId: string, attachmentIndex: number) => void;
  isUploading?: boolean;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const QuestionButtons: React.FC<QuestionButtonsProps> = ({
  questions,
  onAnswerSelect,
  onBatchSubmit,
  selectedAnswers,
  questionAttachments = {},
  onRemoveQuestionAttachment,
  onRetryUpload,
  isUploading = false,
}) => {
  const { theme } = useTheme();
  const isMultiQuestion = questions.length > 1;

  // Local state for batching selections before submit.
  // Each question can have either a text answer OR attachments (mutually exclusive).
  const [pendingSelections, setPendingSelections] = useState<
    Record<string, { answerId: string; answerText: string }>
  >({});

  // Track whether this batch has already been submitted.
  // Initialize from selectedAnswers so that on reload, previously-answered
  // questions render as disabled.
  const [submitted, setSubmitted] = useState(
    () => Object.keys(selectedAnswers).length > 0
  );

  // A question is "answered" if it has a pending selection, a previous answer,
  // or attached photos
  const isQuestionAnswered = useCallback(
    (questionId: string) => {
      if (pendingSelections[questionId]) return true;
      if (selectedAnswers[questionId]) return true;
      const atts = questionAttachments[questionId];
      if (atts && atts.length > 0) return true;
      return false;
    },
    [pendingSelections, selectedAnswers, questionAttachments]
  );

  const allAnswered =
    isMultiQuestion && questions.every((q) => isQuestionAnswered(q.id));

  const handleAnswerTap = useCallback(
    (
      questionId: string,
      questionIndex: number,
      answerIndex: number,
      answerId: string,
      answerText: string
    ) => {
      if (submitted) return;

      // "Attach photo" opens the file picker — it doesn't count as a "selection"
      // in pendingSelections. Instead, the question is considered answered once
      // photos are attached.
      if (answerId === 'upload_now') {
        // Clear any other selection for this question (mutually exclusive)
        setPendingSelections((prev) => {
          const { [questionId]: _, ...rest } = prev;
          return rest;
        });
        onAnswerSelect(
          questionIndex,
          answerIndex,
          answerId,
          answerText,
          questionId
        );
        return;
      }

      // Regular answer — clear attachments concept (selecting a text answer
      // means the user chose something other than "Attach photo")
      // Store the selection, replacing any previous one for this question
      setPendingSelections((prev) => ({
        ...prev,
        [questionId]: { answerId, answerText },
      }));

      // For single-question mode, send immediately
      if (!isMultiQuestion) {
        onAnswerSelect(
          questionIndex,
          answerIndex,
          answerId,
          answerText,
          questionId
        );
      }
    },
    [isMultiQuestion, onAnswerSelect, submitted]
  );

  const handleBatchSubmit = useCallback(() => {
    if (!allAnswered || !onBatchSubmit) return;

    // Build a per-question summary for the combined text
    const answerIds: string[] = [];
    const lines: string[] = [];
    // Build questionId → answerId mapping for persistence
    const selectedAnswersMap: Record<string, string> = {};

    for (const q of questions) {
      const selection = pendingSelections[q.id];
      const atts = questionAttachments[q.id];
      const hasAttachments = atts && atts.length > 0;

      if (selection) {
        answerIds.push(selection.answerId);
        lines.push(`${q.question}: ${selection.answerText}`);
        selectedAnswersMap[q.id] = selection.answerId;
      } else if (hasAttachments) {
        answerIds.push('upload_now');
        lines.push(`${q.question}: Attached ${atts.length} photo(s)`);
        selectedAnswersMap[q.id] = 'upload_now';
      }
    }

    const combinedText = lines.join('\n');
    onBatchSubmit(answerIds, combinedText, selectedAnswersMap);
    setSubmitted(true);
  }, [
    allAnswered,
    pendingSelections,
    questionAttachments,
    questions,
    onBatchSubmit,
  ]);

  return (
    <Box sx={{ mt: 1 }}>
      {questions.map((q, qIdx) => {
        const selection = pendingSelections[q.id];
        const previousAnswer = selectedAnswers[q.id];
        const selectedId = selection?.answerId || previousAnswer;
        const attachments = questionAttachments[q.id] || [];
        const hasAttachments = attachments.length > 0;

        return (
          <Box key={q.id} sx={{ mb: 1.5 }}>
            <Typography variant="body2" sx={{ color: theme.text, mb: 0.75 }}>
              {q.question}
            </Typography>

            {q.answers.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                {q.answers.map((answer, aIdx) => {
                  const isUploadButton = answer.id === 'upload_now';
                  // "Attach photo" is highlighted if photos are attached AND no other answer is selected
                  // Other answers are highlighted if they're the selected answer AND no photos are attached
                  let isSelected = false;
                  if (isUploadButton) {
                    isSelected = hasAttachments && !selectedId;
                  } else {
                    isSelected = selectedId === answer.id && !hasAttachments;
                  }

                  const isDisabled = submitted;

                  return (
                    <Button
                      key={answer.id}
                      size="small"
                      variant={isSelected ? 'contained' : 'outlined'}
                      disabled={isDisabled}
                      onClick={() =>
                        handleAnswerTap(
                          q.id,
                          qIdx,
                          aIdx,
                          answer.id,
                          answer.text
                        )
                      }
                      aria-label={`Answer: ${answer.text}${
                        isSelected ? ', selected' : ''
                      }${
                        isUploadButton && hasAttachments
                          ? ` (${attachments.length} attached)`
                          : ''
                      }`}
                      sx={{
                        borderRadius: '16px',
                        textTransform: 'none',
                        fontSize: 12,
                        px: 1.5,
                        py: 0.5,
                        minWidth: 'auto',
                        backgroundColor: isSelected
                          ? theme.primary
                          : theme.background3,
                        color: isSelected ? theme.textCTA : theme.text,
                        borderColor: 'transparent',
                        '&:hover': {
                          backgroundColor: isSelected
                            ? theme.primaryDark
                            : theme.background2,
                        },
                        '&:disabled': {
                          backgroundColor: isSelected
                            ? theme.primary
                            : theme.background3,
                          color: isSelected ? theme.textCTA : theme.textLight,
                          opacity: 0.7,
                        },
                      }}
                    >
                      {isUploadButton && hasAttachments
                        ? `${answer.text} (${attachments.length})`
                        : answer.text}
                    </Button>
                  );
                })}
              </Box>
            )}

            {/* Per-question attachment thumbnails */}
            {hasAttachments && (
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 0.75,
                  mt: 0.75,
                }}
              >
                {attachments.map((att, attIdx) => (
                  <Box
                    key={`${q.id}-att-${attIdx}`}
                    sx={{
                      position: 'relative',
                      width: 56,
                      height: 56,
                      borderRadius: '8px',
                      overflow: 'hidden',
                      border: `1px solid ${
                        att.uploadStatus === 'error' ? '#f44336' : theme.stroke
                      }`,
                      opacity: att.uploadStatus === 'error' ? 0.7 : 1,
                    }}
                  >
                    {att.uri ? (
                      <img
                        src={att.uri}
                        alt={att.fileName || `Photo ${attIdx + 1}`}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                        }}
                      />
                    ) : (
                      <Box
                        sx={{
                          width: '100%',
                          height: '100%',
                          backgroundColor: theme.background3,
                        }}
                      />
                    )}

                    {/* Spinner overlay for uploading */}
                    {att.uploadStatus === 'uploading' && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: 'rgba(0,0,0,0.4)',
                        }}
                      >
                        <CircularProgress size={20} sx={{ color: '#fff' }} />
                      </Box>
                    )}

                    {/* Error icon with retry button */}
                    {att.uploadStatus === 'error' && (
                      <Tooltip
                        title={
                          att.errorMessage || 'Upload failed — tap to retry'
                        }
                        arrow
                      >
                        <Box
                          onClick={() => onRetryUpload?.(q.id, attIdx)}
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: 'rgba(0,0,0,0.5)',
                            cursor: onRetryUpload ? 'pointer' : 'default',
                            gap: 0.25,
                          }}
                        >
                          <ReplayIcon sx={{ color: '#fff', fontSize: 18 }} />
                          <Typography
                            variant="caption"
                            sx={{ color: '#fff', fontSize: 8, lineHeight: 1 }}
                          >
                            Retry
                          </Typography>
                        </Box>
                      </Tooltip>
                    )}

                    {!submitted && onRemoveQuestionAttachment && (
                      <IconButton
                        size="small"
                        onClick={() => onRemoveQuestionAttachment(q.id, attIdx)}
                        aria-label={`Remove photo ${attIdx + 1}`}
                        sx={{
                          position: 'absolute',
                          top: -4,
                          right: -4,
                          width: 18,
                          height: 18,
                          backgroundColor: 'rgba(0,0,0,0.6)',
                          color: '#fff',
                          '&:hover': {
                            backgroundColor: 'rgba(0,0,0,0.8)',
                          },
                        }}
                      >
                        <CloseIcon sx={{ fontSize: 12 }} />
                      </IconButton>
                    )}
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        );
      })}

      {/* Submit button for multi-question batches */}
      {isMultiQuestion && !submitted && (
        <Box sx={{ mt: 1.5 }}>
          <Button
            size="small"
            variant="contained"
            disabled={!allAnswered || isUploading}
            onClick={handleBatchSubmit}
            endIcon={<SendIcon sx={{ fontSize: 14 }} />}
            aria-label="Submit all answers"
            sx={{
              borderRadius: '16px',
              textTransform: 'none',
              fontSize: 12,
              px: 2,
              py: 0.75,
              backgroundColor: theme.primary,
              color: theme.textCTA,
              '&:hover': {
                backgroundColor: theme.primaryDark,
              },
              '&:disabled': {
                backgroundColor: theme.primaryLight,
                color: theme.textCTA,
                opacity: 0.5,
              },
            }}
          >
            Submit Answers
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default QuestionButtons;
